// services/supabaseAudioService.ts - Tolérance UNIQUEMENT sur la première lettre
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

class SupabaseAudioService {
  private cache = new Map<string, string>()
  private readonly AUDIO_BUCKET = 'jogolinga-audio'
  private readonly SUPPORTED_FORMATS = ['wav', 'mp3', 'ogg']

  getPublicUrl(filePath: string): string {
    if (this.cache.has(filePath)) {
      return this.cache.get(filePath)!
    }

    const { data } = supabase.storage
      .from(this.AUDIO_BUCKET)
      .getPublicUrl(filePath)

    this.cache.set(filePath, data.publicUrl)
    return data.publicUrl
  }

  // 🆕 Fonction pour comparer avec tolérance UNIQUEMENT sur la première lettre
  private matchesWithFirstLetterTolerance(fileName: string, pattern: string): boolean {
    // Si les longueurs sont différentes, pas de match
    if (fileName.length !== pattern.length) {
      return false
    }

    // Vérifier caractère par caractère
    for (let i = 0; i < fileName.length; i++) {
      const fileChar = fileName[i]
      const patternChar = pattern[i]

      // Trouver la position de la première lettre (après le préfixe "XX-")
      const isFirstLetter = i > 0 && fileName[i - 1] === '-' && /[a-zA-Z]/.test(fileChar)

      if (isFirstLetter) {
        // Pour la première lettre: comparaison insensible à la casse
        if (fileChar.toLowerCase() !== patternChar.toLowerCase()) {
          return false
        }
      } else {
        // Pour tous les autres caractères: comparaison STRICTE
        if (fileChar !== patternChar) {
          return false
        }
      }
    }

    return true
  }

  // VERSION OPTIMALE : Recherche large + Filtrage avec tolérance première lettre
  async getWordAudioUrl(languageCode: string, word: string): Promise<string | null> {
    try {
      console.log(`🔍 Recherche audio: "${word}" (${languageCode})`)

      let searchWord = word

      // ÉTAPE 1: Nettoyer les anciens chemins obsolètes
      if (searchWord.startsWith('/audio/')) {
        searchWord = searchWord.replace('/audio/', '')
        console.log(`🧹 Nettoyage chemin: "${word}" → "${searchWord}"`)
      }

      // ÉTAPE 2: Normaliser les espaces en underscores (les fichiers Supabase utilisent _ )
      searchWord = searchWord.replace(/ /g, '_')
      console.log(`🎯 Mot normalisé: "${searchWord}"`)

      // ÉTAPE 3: Recherche avec ilike (large) + Filtrage avec tolérance première lettre
      const prefix = this.getLanguagePrefix(languageCode)
      
      // 🔧 Éviter de dupliquer le préfixe s'il est déjà présent
      const patternsToTry: string[] = []
      
      if (prefix && searchWord.startsWith(prefix)) {
        // Le mot a déjà le préfixe (ex: "Sw-kichwa")
        console.log(`✓ Préfixe "${prefix}" déjà présent dans "${searchWord}"`)
        patternsToTry.push(searchWord)
      } else if (prefix) {
        // Essayer avec et sans préfixe
        patternsToTry.push(`${prefix}${searchWord}`, searchWord)
      } else {
        // Pas de préfixe pour cette langue
        patternsToTry.push(searchWord)
      }
      
      // Tolérance underscores : ajouter les variantes avec espaces à la place des underscores
      // (certains fichiers sont stockés avec des espaces, ex: "Ba-I be fe ka sigi wa" au lieu de "Ba-I_be_fe_ka_sigi_wa")
      const baseVariants = [...patternsToTry]
      baseVariants.forEach(v => {
        if (v.includes('_')) {
          const withSpaces = v.replace(/_/g, ' ')
          if (!patternsToTry.includes(withSpaces)) {
            patternsToTry.push(withSpaces)
          }
        }
      })

      for (const wordVariant of patternsToTry) {
        console.log(`🎯 Tentative avec: "${wordVariant}"`)
        
        for (const format of this.SUPPORTED_FORMATS) {
          const exactPattern = `${wordVariant}.${format}`
          console.log(`🔍 Recherche format ${format.toUpperCase()}: ${exactPattern}`)

          // ✅ Utiliser ilike pour trouver tous les fichiers potentiels
          const { data: audioFiles, error } = await supabase
            .from('audio_files')
            .select('*')
            .eq('language_code', languageCode)
            .ilike('file_path', `%${exactPattern}`)
            .limit(10)

          if (error) {
            console.error(`❌ Erreur recherche ${format}:`, error)
            continue
          }

          if (audioFiles && audioFiles.length > 0) {
            console.log(`📂 ${audioFiles.length} fichier(s) trouvé(s) avec pattern "${exactPattern}"`)
            
            // ✅ FILTRAGE avec tolérance UNIQUEMENT sur la première lettre
            const exactMatch = audioFiles.find(file => {
              // Extraire le nom du fichier du chemin complet
              const fileName = file.file_path.split('/').pop() || ''
              
              // Comparaison avec tolérance première lettre
              const isMatch = this.matchesWithFirstLetterTolerance(fileName, exactPattern)
              
              if (isMatch) {
                console.log(`✅ Correspondance trouvée (tolérance 1ère lettre): ${file.file_path}`)
                console.log(`   Recherché: "${exactPattern}"`)
                console.log(`   Trouvé: "${fileName}"`)
              }
              
              return isMatch
            })

            if (exactMatch) {
              console.log(`✅ Fichier sélectionné: ${exactMatch.file_path}`)
              return await this.createAudioUrl(exactMatch.file_path)
            } else {
              console.log(`⚠️ Aucune correspondance parmi les ${audioFiles.length} fichier(s)`)
              // Debug: afficher ce qui a été trouvé
              audioFiles.forEach(f => {
                const fn = f.file_path.split('/').pop() || ''
                console.log(`   - Trouvé: "${fn}"`)
              })
            }
          }
        }
      }

      // ÉTAPE 4: Recherche alternative si pas trouvé avec préfixe
      if (searchWord.includes('-')) {
        const wordWithoutPrefix = searchWord.split('-')[1]
        console.log(`🔍 Recherche alternative sans préfixe: ${wordWithoutPrefix}`)

        for (const format of this.SUPPORTED_FORMATS) {
          // Essayer avec underscores et avec espaces à la place des underscores
          const alternativePatternBase = `${wordWithoutPrefix}.${format}`
          const alternativePatternWithSpaces = `${wordWithoutPrefix.replace(/_/g, ' ')}.${format}`
          const alternativePatterns = [alternativePatternBase]
          if (alternativePatternWithSpaces !== alternativePatternBase) {
            alternativePatterns.push(alternativePatternWithSpaces)
          }

          for (const alternativePattern of alternativePatterns) {
            const { data: audioFiles, error } = await supabase
              .from('audio_files')
              .select('*')
              .eq('language_code', languageCode)
              .ilike('file_path', `%${alternativePattern}`)
              .limit(10)

            if (error) continue

            if (audioFiles && audioFiles.length > 0) {
              // ✅ FILTRAGE avec tolérance première lettre pour la recherche alternative aussi
              const exactMatch = audioFiles.find(file => {
                const fileName = file.file_path.split('/').pop() || ''
                return this.matchesWithFirstLetterTolerance(fileName, alternativePattern)
              })

              if (exactMatch) {
                console.log(`✅ Correspondance trouvée en mode alternatif: ${exactMatch.file_path}`)
                return await this.createAudioUrl(exactMatch.file_path)
              }
            }
          }
        }
      }

      console.log(`❌ Aucun fichier trouvé pour: "${searchWord}" (${languageCode})`)
      return null

    } catch (error) {
      console.error('💥 Erreur service:', error)
      return null
    }
  }

  // Mapping des préfixes par langue
  private getLanguagePrefix(languageCode: string): string {
    const prefixes: { [key: string]: string } = {
      'li': 'Li-',  // Lingala
      'bm': 'Bm-',  // Bambara  
      'co': 'Co-',  // Copte
      'sw': 'Sw-',  // Swahili
      'wf': 'Wf-',   // Wolof
      'ff': 'Ff-',
      'yo': 'Yo-'   // Yoruba
    }
    return prefixes[languageCode] || ''
  }

  // Création d'URL optimisée
  private async createAudioUrl(filePath: string): Promise<string> {
    if (this.cache.has(filePath)) {
      console.log(`🚀 Cache hit: ${filePath}`)
      return this.cache.get(filePath)!
    }

    try {
      const { data: signedUrlData, error: urlError } = await supabase.storage
        .from(this.AUDIO_BUCKET)
        .createSignedUrl(filePath, 3600)

      if (!urlError && signedUrlData?.signedUrl) {
        console.log(`🔗 URL signée générée: ${filePath}`)
        this.cache.set(filePath, signedUrlData.signedUrl)
        return signedUrlData.signedUrl
      }

      console.log(`⚠️ Fallback URL publique: ${filePath}`)
      return this.getPublicUrl(filePath)
    } catch (error) {
      console.error('Erreur création URL:', error)
      return this.getPublicUrl(filePath)
    }
  }

  // Test de diagnostic avec recherche large + filtrage avec tolérance première lettre
  async diagnosticSearch(languageCode: string, word: string): Promise<void> {
    console.log(`\n🔍 === DIAGNOSTIC pour "${word}" (${languageCode}) ===`)

    const normalizedWord = word.replace(/ /g, '_')
    const prefix = this.getLanguagePrefix(languageCode)
    const patterns = [
      `${normalizedWord}.wav`,
      `${normalizedWord}.mp3`,
      `${normalizedWord}.ogg`,
      `${prefix}${normalizedWord}.wav`,
      `${prefix}${normalizedWord}.mp3`,
      `${prefix}${normalizedWord}.ogg`
    ]

    for (const pattern of patterns) {
      // Recherche large
      const { data, error } = await supabase
        .from('audio_files')
        .select('file_path')
        .eq('language_code', languageCode)
        .ilike('file_path', `%${pattern}`)
        .limit(5)

      if (error) {
        console.log(`  ${pattern}: ❌ Erreur - ${error.message}`)
        continue
      }

      if (data && data.length > 0) {
        // Filtrage avec tolérance première lettre
        const exactMatch = data.find(file => {
          const fileName = file.file_path.split('/').pop() || ''
          return this.matchesWithFirstLetterTolerance(fileName, pattern)
        })

        if (exactMatch) {
          console.log(`  ${pattern}: ✅ Trouvé (tolérance 1ère lettre) - ${exactMatch.file_path}`)
        } else {
          console.log(`  ${pattern}: ⚠️ Trouvé (partiel seulement) - ${data.length} fichier(s)`)
          data.forEach((f, i) => {
            const fn = f.file_path.split('/').pop() || ''
            console.log(`    ${i + 1}. ${fn}`)
          })
        }
      } else {
        console.log(`  ${pattern}: ❌ Non trouvé`)
      }
    }
    console.log(`=== FIN DIAGNOSTIC ===\n`)
  }

  async preloadCategoryAudios(languageCode: string, category: string): Promise<number> {
    console.log(`📄 Préchargement: ${languageCode}/${category}`)
    return 0
  }

  async testConnection(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('audio_files')
        .select('file_path')
        .eq('language_code', 'li')
        .limit(5)

      console.log(`🔌 Test connexion:`)
      if (data) {
        data.forEach((f, i) => console.log(`  ${i+1}. ${f.file_path}`))
      }
      
      return !error && !!data
    } catch (error) {
      console.error('Test connexion échoué:', error)
      return false
    }
  }
}

export default new SupabaseAudioService()